/* global globalThis */
const KEY_PREFIX = 'sportsnew.v1.';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const optionalText = value => (
  value === undefined || value === null || value === '' ? null : String(value)
);

const optionalNumber = value => {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const optionalExpectedPrice = value => {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 1) return null;
  return Number(number.toFixed(2));
};

const bytesToUuid = bytes => {
  const value = [...bytes].map(byte => byte.toString(16).padStart(2, '0')).join('');
  return [value.slice(0, 8), value.slice(8, 12), value.slice(12, 16), value.slice(16, 20), value.slice(20)].join('-');
};

const secureUuid = cryptoProvider => {
  const source = cryptoProvider ?? globalThis.crypto;
  if (typeof source?.randomUUID === 'function') {
    const uuid = source.randomUUID();
    if (UUID_PATTERN.test(uuid)) return uuid.toLowerCase();
  }
  if (typeof source?.getRandomValues === 'function') {
    const bytes = source.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return bytesToUuid(bytes);
  }
  const error = new Error('Secure UUID generation is unavailable');
  error.code = 'SPORTSNEW_SECURE_IDEMPOTENCY_UNAVAILABLE';
  throw error;
};

const canonicalSelection = selection => ({
  matchId: optionalText(selection?.match_id ?? selection?.matchId),
  providerMatchId: optionalText(selection?.providerMatchId ?? selection?.provider_match_id),
  marketId: optionalText(selection?.marketId ?? selection?.market_id),
  oddStateId: Number(selection?.oddStateId),
  expectedPriceDecimal: optionalExpectedPrice(selection?.expectedPriceDecimal),
  providerOddId: optionalText(selection?.providerOddId),
  oddIdentityKey: optionalText(selection?.oddIdentityKey),
  shortOddId: optionalText(selection?.shortOddId),
  canonicalSelectionKey: optionalText(selection?.canonicalSelectionKey),
  canonicalLineKey: optionalText(selection?.canonicalLineKey),
  canonicalPairKey: optionalText(selection?.canonicalPairKey),
  points: optionalNumber(selection?.points),
  line: optionalNumber(selection?.line),
  selectionLine: optionalText(selection?.selectionLine),
  groupingKey: optionalText(selection?.groupingKey),
});

export const buildSportsnewPlacementCommandIdentity = ({
  userId,
  accountGeneration,
  payload = {},
}) => ({
  userId: Number(userId),
  accountGeneration: optionalNumber(accountGeneration),
  betType: String(payload.bet_type ?? payload.betType ?? '').trim().toUpperCase(),
  bettingCategory: String(
    payload.betting_category ?? payload.bettingCategory ?? '',
  ).trim().toUpperCase(),
  betMode: String(payload.betMode || '').trim().toLowerCase(),
  displayScope: optionalText(payload.displayScope ?? payload.scope),
  stake: optionalNumber(payload.stake),
  isBonusRecover: payload.isBonusRecover === true || payload.is_bonus_recover === true,
  selections: (Array.isArray(payload.selections) ? payload.selections : [])
    .map(canonicalSelection)
    .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))),
});

export const createSportsnewPlacementKey = ({ cryptoProvider } = {}) => {
  const uuid = secureUuid(cryptoProvider);
  const key = `${KEY_PREFIX}${uuid}`;
  if (key.length >= 100 || !key.endsWith(uuid)) {
    throw new Error('Sportsnew placement key contract violation');
  }
  return key;
};

export const createSportsnewPlacementChallenge = ({ cryptoProvider } = {}) => (
  secureUuid(cryptoProvider)
);

export const fingerprintSportsnewPlacementCommand = async (
  input,
  { cryptoProvider } = {},
) => {
  const source = cryptoProvider ?? globalThis.crypto;
  if (typeof source?.subtle?.digest !== 'function') {
    const error = new Error('Secure command fingerprinting is unavailable');
    error.code = 'SPORTSNEW_COMMAND_FINGERPRINT_UNAVAILABLE';
    throw error;
  }
  const canonical = JSON.stringify(buildSportsnewPlacementCommandIdentity(input));
  const digest = await source.subtle.digest('SHA-256', new TextEncoder().encode(canonical));
  return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
};

export const SPORTSNEW_PLACEMENT_KEY_PREFIX = KEY_PREFIX;
export const SPORTSNEW_PLACEMENT_UUID_PATTERN = UUID_PATTERN;
